// File: App.tsx
import { useState, createContext, useEffect, useRef, useMemo, useCallback} from 'react';
import type { ChangeEvent } from 'react';
import BerechnungsButtonCpSolver from './BerechnungsButtonCpSolver'
import styles from "./styles/App.module.css"
import Table from './Table';
import StudentsInfoPanel from './StudentsInfoPanel/StudentsInfoPanel';
import SelectTimeRange from './SelectTimeRange';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { DndProvider } from 'react-dnd';
import ExportPdfButton from './ExportPdfButton';
import LoadScheduleButton from './LoadScheduleButton';
import SendPdfButton from './SendPdfButton';
import SaveScheduleModal from './teacher/SaveScheduleModal';
import LoadScheduleModal from './teacher/LoadScheduleModal';
import { useAuth } from './AuthContext';
import FontFaceObserver from 'fontfaceobserver';
import ApiClient from './utils/apiClient';
import SendScheduleConfirmModal from './teacher/SendScheduleConfirmModal';
import { useUnsavedChanges } from './contexts/UnsavedChangesContext';
import { toast } from 'react-toastify';
import Sidebar from './components/Sidebar';
import SaveBar from './components/SaveBar';
import ReleaseScheduleDialog from './components/ReleaseScheduleDialog';
import ConfirmModal from './components/ConfirmModal';
import { mergeStudentsWithSchedule } from './utils/scheduleUtils';
import { convertMemberIdsToNames } from './utils/scheduleHelpers';
import AdvancedWeightSettings from './components/AdvancedWeightSettings';
import SolverProfilePicker from './components/SolverProfilePicker';
import { buildWeights, FAV_TIMES_DEFAULT, FAV_TIMES_OPTIONS, FAV_TIMES_ORDER } from './utils/solverProfiles';
import faceScanSquareIcon from './img/face-scan-square-svgrepo-com.svg';
import { dayTimeToSlot, slotToDayTime, fromScheduleMap, toGermanDayMap, ALL_TIME_SLOTS, ALL_DAY_NAMES_DE } from './utils/timeSlots';
import type { BerechnungsButtonHandle } from './BerechnungsButtonCpSolver';
import type { SaveScheduleModalHandle, SavedSchedule } from './teacher/SaveScheduleModal';
import type { LoadedScheduleData } from './teacher/LoadScheduleModal';
import type { PanelStudent } from './StudentsInfoPanel/StudentPanel';
import type { DisplaySchedule } from './utils/scheduleHelpers';
import type { SolverProfileId, FavTimesLevel } from './utils/solverProfiles';

/** Zell-Highlighting im Schedule-Grid (Provider: App, Consumer: Panels/Table). */
export type ToggleHighlightCells = (
  timesObj: any,
  highlight: boolean,
  highlightClass: string,
  prefix?: string,
) => void;

export interface TimePreferencesContextValue {
  timePreferences?: Record<string, string> | null;
  onTimePreferenceChange: (memberId: string, preference: string) => void;
}

/** Kurs-Objekt, wie es durch die Scheduling-Seiten gereicht wird. */
export interface AppSelectedClass {
  class_id: string;
  name?: string | null;
  room?: string | null;
  favorite_times_mode?: string | null;
  available_times?: Record<string, unknown> | null;
  time_range_for_students?: Record<string, unknown> | null;
  [key: string]: unknown;
}

// Wie in den JS-Zeiten: Default ist undefined — Nutzung ohne Provider crasht
// beim Aufruf (bewusst identisch gehalten, daher der Cast statt `| undefined`).
export const HighlightContext = createContext<ToggleHighlightCells>(undefined as never)
export const StudentsListContext = createContext<unknown>(undefined)
export const TimePreferencesContext = createContext<TimePreferencesContextValue>(undefined as never)

// Werte kommen aus dem geteilten timeSlots-Modul (ersetzt 3 identische
// Literal-Kopien in App.js/student/StudentApp.js/multiview/AppMV.js);
// Re-Export unter den bisherigen Namen, damit bestehende Importe unverändert
// bleiben (`import { completeTimesList, completeDaysList } from './App'` u.a.).
export const completeTimesList = ALL_TIME_SLOTS;
export const completeDaysList = ALL_DAY_NAMES_DE;

interface AppProps {
  schedule?: DisplaySchedule | null;
  studentsList?: PanelStudent[] | null;
  scheduleName?: string | null;
  selectedClass?: AppSelectedClass | null;
  classMembers?: unknown[] | null;
  onStudentsMerge?: unknown;
  onScheduleUpdate?: (schedule: DisplaySchedule, updatedEntry: unknown, members?: unknown, newStudentsList?: unknown) => void;
  onLockToggle?: (memberId: string) => void;
  onCalculationComplete?: (timetable: unknown, lockedStudents: unknown) => void;
  teacherTimes?: unknown;
  timePreferences?: Record<string, string> | null;
  onTimePreferenceChange?: (memberId: string, preference: string) => void;
  loadedScheduleId?: string | null;
  loadedScheduleIsPrimary?: boolean;
  /** Hat der Kurs irgendeinen freigegebenen Plan? (Hinweis nach dem ersten Speichern) */
  classHasReleasedPlan?: boolean;
  onScheduleIdChange?: (id: string) => void;
  onPrimaryStatusChange?: (isPrimary: boolean) => void;
  onScheduleNameChange?: (name: string) => void;
}

function App({
  schedule: initialSchedule,
  studentsList: initialStudentsList,
  scheduleName: initialScheduleName,
  selectedClass,
  classMembers,
  onStudentsMerge,
  onScheduleUpdate,
  onLockToggle,
  onCalculationComplete,
  teacherTimes,
  timePreferences,
  onTimePreferenceChange,
  loadedScheduleId,
  loadedScheduleIsPrimary,
  classHasReleasedPlan,
  onScheduleIdChange,
  onPrimaryStatusChange,
  onScheduleNameChange
}: AppProps) {
  const { hasUnsavedChanges, setHasUnsavedChanges } = useUnsavedChanges();

  // Variante A: keine lokalen Kopien; Props direkt verwenden (Single Source of Truth)
  const schedule = initialSchedule || {};
  const studentsList = initialStudentsList || null;
  const scheduleName = initialScheduleName || '';

  const timesList = completeTimesList

  const [wiggleRoom, setWiggleRoom] = useState<number | string>(3)
  const handleWiggleChange = ({target}: ChangeEvent<HTMLInputElement>) => {
    return setWiggleRoom(target.value)
  }

  // Neue Optionen analog MultiView
  const [allowBlindAssignment, setAllowBlindAssignment] = useState(false)
  const [teacherTimeExtension, setTeacherTimeExtension] = useState(0)
  
  // Solver-Profile
  const [selectedProfile, setSelectedProfile] = useState<SolverProfileId>('ausgewogen')
  const [favTimesLevel, setFavTimesLevel] = useState<FavTimesLevel>(FAV_TIMES_DEFAULT)
  const berechnenRef = useRef<BerechnungsButtonHandle>(null)

  const handleProfileChange = useCallback((profileId: SolverProfileId) => {
    setSelectedProfile(profileId)
    setAdvancedWeights(prev => buildWeights(profileId, favTimesLevel))
  }, [favTimesLevel])

  const handleFavTimesChange = useCallback((level: FavTimesLevel) => {
    setFavTimesLevel(level)
    setAdvancedWeights(prev => buildWeights(selectedProfile, level))
  }, [selectedProfile])

  // Erweiterte Weight-Einstellungen (absolute Werte 0-100000)
  // Initial auf "Ausgewogen"-Profil gesetzt (rebalancierte Gewichte)
  const [advancedWeights, setAdvancedWeights] = useState<Record<string, number>>(() => buildWeights('ausgewogen', FAV_TIMES_DEFAULT))

  const wrappedOnCalculationComplete = useCallback((...args: unknown[]) => {
    // Wie im JS-Original ohne Guard — ohne Callback soll es weiterhin knallen.
    ;(onCalculationComplete as (...a: unknown[]) => void)(...args)
  }, [onCalculationComplete])

  const [timeIndexRange, setTimeIndexRange] = useState({
    start: 32, // 8:00 Uhr als Default
    end: 76    // 19:00 Uhr als Default
  });

  const slicedTimesList = useMemo(
    () => timesList.slice(timeIndexRange.start, timeIndexRange.end),
    [timeIndexRange.start, timeIndexRange.end, timesList]
  );

  const handleTimeRangeChange = useCallback(({ start = 32, end = 76 }: { start?: string | number; end?: string | number }) => {
    // Konvertiere die Zeitstrings in Indizes
    const startIndex = timesList.findIndex(time => time === start);
    const endIndex = timesList.findIndex(time => time === end);
    
    if (startIndex !== -1 && endIndex !== -1) {
      setTimeIndexRange({
        start: startIndex,
        end: endIndex + 1 // +1 weil wir die Endzeit einschließen wollen
      });
    }
  }, [timesList]);

  // F5: useCallback, damit der HighlightContext-Value stabil bleibt und Consumer (Table)
  // nicht bei jedem App-Render re-rendern. timesList ist eine Modul-Konstante (stabil).
  const toggleHighlightCells = useCallback<ToggleHighlightCells>((timesObj, highlight, highlightClass, prefix = '') => {
    // console.log("toggleHighlightCells - timesObj:", timesObj);
    for (let i = 0; i < Object.keys(timesObj).length; i++) {
      // console.log("currentTimesKey:", Object.keys(timesObj)[i])
      const currentTimesKey = Number(Object.keys(timesObj)[i])
      const timesOfCurrentDay = timesObj[currentTimesKey]
      // console.log("timesOfCurrentDay:", timesOfCurrentDay)
      // console.log("timesList:", timesList)

      if (!timesOfCurrentDay) {
        continue
      }

      for (let j = 0; j < timesOfCurrentDay.length; j++) {
          const rawTime = timesOfCurrentDay[j]
          // dayTimeToSlot(0, ...) normalisiert führende Nullen ("08.00"=="8.00")
          // statt sie manuell zu stripen, und liefert bei ungültigem Format null.
          const slot = dayTimeToSlot(0, rawTime)
          const time = slot !== null ? slotToDayTime(slot)[1] : rawTime

          if (slot === null || !timesList.find(element => element === time)) {
                console.log("time not found in timesList:", time)
                continue
          }
          
          let currentCellId = currentTimesKey + "_" + time
          if (prefix !== '') {
            currentCellId = prefix + currentCellId
          }
          
          const currentCell = document.getElementById(currentCellId)
          
          if (!currentCell) {
            continue
          }
          
          if (highlight) {
            if (!currentCell.classList.contains(highlightClass)) currentCell.classList.add(highlightClass)
          }
          else {
            if (currentCell.classList.contains(highlightClass)) currentCell.classList.remove(highlightClass)
          }
      }
    }
  }, [timesList])

  const handleScheduleSave = (savedScheduleData: SavedSchedule) => {
    console.log('Schedule saved:', savedScheduleData);
    toast.success('Stundenplan erfolgreich gespeichert');
    setHasUnsavedChanges(false);
    // Sanfte Erinnerung statt Automatik (UX-Plan 3.5 f): einmal pro Kurs und Sitzung
    if (!classHasReleasedPlan && !savedScheduleData.is_primary && selectedClass?.class_id) {
      const key = `releaseHintShown:${selectedClass.class_id}`;
      try {
        if (!window.sessionStorage.getItem(key)) {
          window.sessionStorage.setItem(key, '1');
          toast.info('Studierende sehen diesen Plan noch nicht. Gib ihn frei, wenn er fertig ist.', { autoClose: 6000 });
        }
      } catch { /* sessionStorage nicht verfügbar */ }
    }
  };

  // Freigeben / Freigabe aufheben (UX-Plan 3.5): eigener Knopf mit Bestätigungsdialog,
  // getrennt vom Speichern. Ohne gespeicherten Stand gibt es nichts freizugeben.
  const [releaseDialog, setReleaseDialog] = useState<'release' | 'unrelease' | null>(null);
  // Speichern eines freigegebenen Plans: sofort übernehmen oder als neuen Entwurf?
  const [showSaveReleasedChoice, setShowSaveReleasedChoice] = useState(false);
  const canRelease = !!loadedScheduleId && !hasUnsavedChanges;

  const saveViaModal = async (): Promise<boolean> => {
    if (!saveScheduleModalRef.current) return false;
    const result = await saveScheduleModalRef.current.saveSchedule();
    return !!result;
  };

  const saveAsNewDraft = async (): Promise<boolean> => {
    if (!saveScheduleModalRef.current) return false;
    const draftName = `${scheduleName || 'Stundenplan'} – Entwurf ${new Date().toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}`;
    const result = await saveScheduleModalRef.current.saveSchedule(null, draftName);
    if (result && typeof result === 'object') {
      onScheduleNameChange?.(draftName);
    }
    return !!result;
  };

  const [isLoadScheduleModalOpen, setIsLoadScheduleModalOpen] = useState(false);
  const { userRole } = useAuth();

  const handleScheduleLoad = (loadedData: LoadedScheduleData) => {
    console.log("[App.js handleScheduleLoad] Received raw data:", loadedData);
    const { rawScheduleTimes, members, name, id, is_primary } = loadedData;

    // Update schedule name, id, and is_primary via callbacks
    if (onScheduleNameChange && name) {
      onScheduleNameChange(name);
    }
    if (onScheduleIdChange && id) {
      onScheduleIdChange(id);
    }
    if (onPrimaryStatusChange !== undefined) {
      onPrimaryStatusChange(is_primary || false);
    }

    // Konvertiere Schedule-Format (Backend -> Frontend) über das geteilte
    // timeSlots-Modul statt einer eigenen, unshared Implementierung — behebt
    // den früheren "hidden, backend-invisible coupling point" zur Tupel-Form
    // aus der DB (Gegenstück: teacher/SaveScheduleModal.js::saveSchedule).
    let frontendSchedule: DisplaySchedule = {};
    if (rawScheduleTimes && typeof rawScheduleTimes === 'object') {
        frontendSchedule = toGermanDayMap(fromScheduleMap(rawScheduleTimes as never));
        // Alle 7 Tage als Keys sicherstellen (auch ohne Zuweisungen), wie zuvor.
        completeDaysList.forEach(dayName => {
            if (!frontendSchedule[dayName]) frontendSchedule[dayName] = {};
        });
    } else {
        console.warn("[App.js handleScheduleLoad] rawScheduleTimes is not in the expected format or is null/undefined.");
    }


    console.log("[App.js handleScheduleLoad] Converted frontendSchedule:", frontendSchedule);

    // *** HIER: Berechne studentsList EINMAL ***
    const newStudentsList = mergeStudentsWithSchedule((members || []) as never, frontendSchedule);
    console.log("[App.js handleScheduleLoad] Merged newStudentsList:", newStudentsList);


    // *** HIER: Übergib das BERECHNETE newStudentsList an TeacherSchedulingProcess ***
    if (onScheduleUpdate) {
        // Übergabe: schedule, updatedEntry (null), loadedMembers, newStudentsList
        onScheduleUpdate(frontendSchedule, null, members, newStudentsList);
        console.log("[App.js handleScheduleLoad] Called onScheduleUpdate in Parent with calculated studentsList.");
    } else {
         console.error("[App.js handleScheduleLoad] props.onScheduleUpdate is not defined!");
    }
  };

  useEffect(() => {
    const font = new FontFaceObserver('Comfortaa');
    
    font.load().then(() => {
      document.documentElement.classList.add('fonts-loaded');
    }).catch((err) => {
      console.warn('Font loading failed', err);
    });
  }, []);

  const [showSendConfirmModal, setShowSendConfirmModal] = useState(false);
  const [pendingPdfBlob, setPendingPdfBlob] = useState<Blob | null>(null);

  const getDefaultScheduleName = () => {
    const now = new Date();
    return `Stundenplan ${now.toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })}`;
  };

  const saveScheduleModalRef = useRef<SaveScheduleModalHandle>(null);
  
  const handleSendToStudents = async () => {
    //#TODO: memberIds der Klassenmitglieder zu Namen konvertieren
    const scheduleWithNames = convertMemberIdsToNames(schedule, studentsList);
    console.log("HandleSendToStudents: scheduleWithNames:", scheduleWithNames);
    try {
      const pdfResponse = await ApiClient.post('export-pdf-alternative', { 
        schedule: scheduleWithNames,
        timeRange: {
          start: timesList[timeIndexRange.start],
          end: timesList[timeIndexRange.end]
        }
      });

      if (!pdfResponse.ok) {
        throw new Error('PDF-Generierung fehlgeschlagen');
      }

      const pdfBlob = await pdfResponse.blob();
      setPendingPdfBlob(pdfBlob);
      setShowSendConfirmModal(true);
    } catch (error) {
      console.error('Fehler:', error);
      alert('Fehler beim Generieren der PDF');
    }
  };

  const handleConfirmSend = async ({ name, description, classId }: { name: string; description: string; classId: string }) => {
    try {
      if (saveScheduleModalRef.current && hasUnsavedChanges) {
        await saveScheduleModalRef.current.saveSchedule(null, name, description);
      }

      const formData = new FormData();
      formData.append('pdf', new Blob([pendingPdfBlob!], { type: 'application/pdf' }), 'stundenplan.pdf');
      formData.append('class_id', classId);

      const response = await ApiClient.postFormData(
        'send-schedule-to-students',
        formData
      );
      // ApiClient wirft bei !ok - kein manueller Check nötig
      const data = await response.json();
      alert(`Stundenplan wurde erfolgreich gespeichert und an ${data.recipient_count} Empfänger versendet!`);
    } catch (error) {
      console.error('Fehler:', error);
      alert('Fehler beim Versenden oder Speichern');
    } finally {
      setShowSendConfirmModal(false);
      setPendingPdfBlob(null);
    }
  };

  return (
    <>
      <div className={styles.mainContainer}>
      
        <DndProvider backend={HTML5Backend}>
          <HighlightContext.Provider value={toggleHighlightCells}>
            <TimePreferencesContext.Provider value={{ timePreferences, onTimePreferenceChange } as TimePreferencesContextValue}>
            <Sidebar>
              <div className={styles.sidebarContent}>
                <SelectTimeRange 
                  onChange={handleTimeRangeChange}
                  timesList={timesList}
                  initialStart={timeIndexRange.start}
                  initialEnd={timeIndexRange.end}
                />
              </div>
            </Sidebar>

            <div className={styles.mainContent}>
              <div className={styles.buttonsAndSettingsContainerContainer}>
              <SaveScheduleModal
                ref={saveScheduleModalRef}
                schedule={schedule}
                onSaveSuccess={handleScheduleSave}
                loadedScheduleName={scheduleName}
                selectedClass={selectedClass}
                loadedScheduleId={loadedScheduleId}
                loadedScheduleIsPrimary={loadedScheduleIsPrimary}
                onScheduleIdChange={onScheduleIdChange}
                onPrimaryStatusChange={onPrimaryStatusChange}
              />
                <div className={styles.buttonsAndSettingsContainer}>
                  {studentsList && Object.keys(studentsList).length > 0 && (userRole === 'teacher' || userRole === 'admin') && (
                    <>
                    <div className={styles.calculationGroup}>
                      <BerechnungsButtonCpSolver
                        ref={berechnenRef}
                        wiggleRoom={wiggleRoom}
                        allowBlindAssignment={allowBlindAssignment}
                        teacherTimeExtension={teacherTimeExtension}
                        advancedWeights={advancedWeights}
                        // Neuversuch aus dem Fehler-Dialog: Einstellungen auch in
                        // den sichtbaren Reglern übernehmen, nicht nur im Request.
                        onApplySettings={({ wiggleRoom: w, allowBlindAssignment: b, teacherTimeExtension: t }) => {
                          if (w !== undefined) setWiggleRoom(w);
                          if (b !== undefined) setAllowBlindAssignment(b);
                          if (t !== undefined) setTeacherTimeExtension(t);
                        }}
                        onCalculationComplete={wrappedOnCalculationComplete}
                        isLoading={!studentsList}
                        selectedClass={selectedClass}
                        hasData={!!studentsList && Object.keys(studentsList).length > 0}
                        studentsList={studentsList}
                        schedule={schedule}
                      />
                      <SolverProfilePicker
                        mode="toggle"
                        selectedProfile={selectedProfile}
                        onProfileChange={handleProfileChange}
                        advancedWeights={advancedWeights}
                      />
                      {selectedClass?.favorite_times_mode && selectedClass.favorite_times_mode !== 'disabled' && (
                        <label className={styles.favTimesContainer} title="Wie stark die Lieblingszeiten der Studierenden zählen">
                          <span className={styles.toolbarFieldLabel}>Lieblingszeiten</span>
                          <select
                            className={styles.favTimesSelect}
                            value={favTimesLevel}
                            onChange={(e) => handleFavTimesChange(e.target.value as FavTimesLevel)}
                          >
                            {FAV_TIMES_ORDER.map(id => (
                              <option key={id} value={id}>{FAV_TIMES_OPTIONS[id].label}</option>
                            ))}
                          </select>
                        </label>
                      )}
                      <label className={styles.wiggleRoomContainer} title="So viele Personen dürfen beim Berechnen unverplant bleiben, wenn nicht alle passen">
                        <img
                          src={faceScanSquareIcon}
                          alt=""
                          aria-hidden="true"
                          className={styles.wiggleRoomIcon}
                        />
                        <span className={styles.toolbarFieldLabel}>Unverplant max.</span>
                        <input 
                          type="number" 
                          min="0" 
                          max="99" 
                          step="1" 
                          name="wiggleRoom" 
                          value={wiggleRoom} 
                          className={styles.wiggleRoom}
                          onChange={handleWiggleChange}
                        />
                      </label>
                      
                      <AdvancedWeightSettings
                        weights={advancedWeights}
                        onWeightsChange={setAdvancedWeights}
                        isMultiView={false}
                        allowBlindAssignment={allowBlindAssignment}
                        onAllowBlindAssignmentChange={setAllowBlindAssignment}
                        teacherTimeExtension={teacherTimeExtension}
                        onTeacherTimeExtensionChange={setTeacherTimeExtension}
                      />
                      
                      <div className={styles.buttonsContainer}>
                        {loadedScheduleIsPrimary ? (
                          <button
                            type="button"
                            className={styles.unreleaseButton}
                            onClick={() => setReleaseDialog('unrelease')}
                            disabled={!canRelease}
                            title={canRelease ? 'Studierende sehen danach keinen Termin mehr' : 'Erst speichern, dann Freigabe ändern'}
                          >
                            Freigabe aufheben
                          </button>
                        ) : (
                          <button
                            type="button"
                            className={styles.releaseButton}
                            onClick={() => setReleaseDialog('release')}
                            disabled={!canRelease}
                            title={canRelease ? 'Studierende sehen dann ihren Termin aus diesem Plan' : 'Erst speichern, dann freigeben'}
                          >
                            An Studierende freigeben
                          </button>
                        )}
                        <LoadScheduleButton 
                          onClick={() => setIsLoadScheduleModalOpen(true)}
                        />
                        <ExportPdfButton
                          schedule={convertMemberIdsToNames(schedule, studentsList)}
                          studentsList={studentsList}
                          timeRange={{
                            start: timesList[timeIndexRange.start],
                            end: timesList[timeIndexRange.end]
                          }}
                          selectedClass={selectedClass}
                        />
                        <SendPdfButton 
                          onClick={handleSendToStudents}
                          disabled={!schedule || !studentsList}
                        />
                      </div>
                    </div>
                    </>
                  )}
                  {!studentsList && userRole === 'teacher' && (
                  <LoadScheduleButton 
                    onClick={() => setIsLoadScheduleModalOpen(true)}
                  />
                  )}
                </div>
                
              </div>

              <div className={`${styles.tableAndPanelContainer} ${styles.docked}`}>
                {(schedule && Object.keys(schedule).length > 0) || (studentsList && studentsList.length > 0)? (
                      <>
                        <Table
                          schedule={schedule}
                          studentsList={studentsList}
                          onScheduleUpdate={onScheduleUpdate}
                          onLockToggle={onLockToggle}
                          timeIndexOffset={timeIndexRange.start}
                          timesList={slicedTimesList}
                          timeIndexRange={timeIndexRange}
                          classMembers={classMembers}
                          classId={selectedClass?.class_id}
                          selectedClass={selectedClass}
                        />
                      </>
                   ) : (
                      <div className={styles.placeholder}>Stundenplan ist leer oder wird geladen...</div>
                   )}
                <div className={styles.infoPanelsContainer}>
                  { selectedClass && studentsList && (
                    <StudentsInfoPanel
                      key={`students-panel-${selectedClass?.class_id}-${studentsList.length}`}
                      schedule={schedule}
                      classId={selectedClass.class_id}
                      studentsList={studentsList}
                      classMembers={classMembers}
                      onStudentsMerge={onStudentsMerge}
                      onLockToggle={onLockToggle}
                      teacherTimes={teacherTimes as never}
                    />
                  )}
                </div>
              </div>
              {studentsList && studentsList.length > 0 && (userRole === 'teacher' || userRole === 'admin') && (
                <SaveBar
                  dirty={hasUnsavedChanges}
                  onSave={async () => {
                    if (loadedScheduleId && loadedScheduleIsPrimary) {
                      // Änderung an einem freigegebenen Plan: nachfragen statt still überschreiben
                      setShowSaveReleasedChoice(true);
                      return true;
                    }
                    return saveViaModal();
                  }}
                  saveLabel="Plan speichern"
                  hint={loadedScheduleIsPrimary
                    ? `„${scheduleName}“ ist für Studierende freigegeben`
                    : loadedScheduleId
                      ? `Entwurf „${scheduleName}“ – Studierende sehen ihn noch nicht`
                      : 'Speichern legt einen Entwurf an – freigeben kannst du ihn danach'}
                />
              )}
            </div>
            </TimePreferencesContext.Provider>
          </HighlightContext.Provider>
        </DndProvider>

        {isLoadScheduleModalOpen && (
          <LoadScheduleModal 
            onLoad={handleScheduleLoad}
            onClose={() => setIsLoadScheduleModalOpen(false)}
            classId={selectedClass!.class_id}
          />
        )}

        {releaseDialog && selectedClass && loadedScheduleId && (
          <ReleaseScheduleDialog
            classId={selectedClass.class_id}
            scheduleId={loadedScheduleId}
            scheduleName={scheduleName}
            release={releaseDialog === 'release'}
            onClose={() => setReleaseDialog(null)}
            onDone={(released) => {
              setReleaseDialog(null);
              onPrimaryStatusChange?.(released);
            }}
          />
        )}

        {showSaveReleasedChoice && (
          <ConfirmModal
            isOpen={showSaveReleasedChoice}
            onClose={async () => {
              // „Abbrechen“ = als neuen Entwurf speichern (die sichere Variante bleibt Standard)
              setShowSaveReleasedChoice(false);
              await saveAsNewDraft();
            }}
            onConfirm={async () => {
              setShowSaveReleasedChoice(false);
              await saveViaModal();
            }}
            title="Dieser Plan ist freigegeben"
            message={`„${scheduleName}“ sehen deine Studierenden bereits. Änderung sofort für sie übernehmen – oder lieber als neuen Entwurf speichern und erst später freigeben? (Abbrechen = neuer Entwurf)`}
            buttonText="Sofort übernehmen"
          />
        )}

        {showSendConfirmModal && (
          <SendScheduleConfirmModal
            onConfirm={handleConfirmSend}
            onCancel={() => {
              setShowSendConfirmModal(false);
              setPendingPdfBlob(null);
            }}
            defaultName={getDefaultScheduleName()}
            selectedClass={selectedClass!}
          />
        )}
      </div>

    </>
  );
}

export default App;
